import { getMaImageAltTexts, updateMaImageAltText } from '@/lib/zybooks-api';
import type { Node as PMNode } from '@tiptap/pm/model';
import type { Editor } from '@tiptap/react';

const MA_IMAGE_NODE_TYPES = new Set(['image', 'zyInlineImage']);

export function syncMaImageAltText(maImageId: unknown, altText: string): void {
    const trimmedAlt = altText.trim();
    if (!trimmedAlt || typeof maImageId !== 'number' || maImageId <= 0) {
        return;
    }

    void updateMaImageAltText(maImageId, trimmedAlt).catch(error => {
        console.error('Error syncing MA image alt text:', error);
    });
}

/** @deprecated Use syncMaImageAltText */
export const backfillMaImageAltTextIfEmpty = syncMaImageAltText;

export function collectMaImageIdsFromDoc(doc: PMNode): number[] {
    const ids = new Set<number>();
    doc.descendants(node => {
        if (!MA_IMAGE_NODE_TYPES.has(node.type.name)) {
            return;
        }
        const maImageId = node.attrs?.maImageId;
        if (typeof maImageId === 'number' && maImageId > 0) {
            ids.add(maImageId);
        }
    });
    return [...ids];
}

export interface MaImageAltTextUpdate {
    pos: number;
    alt: string;
}

export function buildMaImageAltTextUpdates(doc: PMNode, altTexts: Record<number, string>): MaImageAltTextUpdate[] {
    const updates: MaImageAltTextUpdate[] = [];
    doc.descendants((node, pos) => {
        if (!MA_IMAGE_NODE_TYPES.has(node.type.name)) {
            return;
        }
        const maImageId = node.attrs?.maImageId;
        if (typeof maImageId !== 'number' || maImageId <= 0) {
            return;
        }
        const dbAlt = altTexts[maImageId];
        if (!dbAlt || dbAlt === node.attrs.alt) {
            return;
        }
        updates.push({ pos, alt: dbAlt });
    });
    return updates;
}

export async function syncMaImageAltTextInEditor(editor: Editor): Promise<void> {
    const doc = editor.state.doc;
    const maImageIds = collectMaImageIdsFromDoc(doc);
    if (maImageIds.length === 0) {
        return;
    }

    let altTexts: Record<number, string>;
    try {
        altTexts = await getMaImageAltTexts(maImageIds);
    } catch (error) {
        console.error('Error syncing MA image alt text from database:', error);
        return;
    }

    const updates = buildMaImageAltTextUpdates(doc, altTexts);
    if (updates.length === 0) {
        return;
    }

    const tr = editor.state.tr;
    for (const { pos, alt } of [...updates].sort((left, right) => right.pos - left.pos)) {
        const node = tr.doc.nodeAt(pos);
        if (!node || !MA_IMAGE_NODE_TYPES.has(node.type.name)) {
            continue;
        }
        tr.setNodeMarkup(pos, undefined, { ...node.attrs, alt });
    }

    if (tr.docChanged) {
        tr.setMeta('addToHistory', false);
        editor.view.dispatch(tr);
    }
}
